(() => {
  const loader = document.currentScript;
  if (!loader) return;

  const launcher = document.querySelector('[data-gpused-assistant-launcher]');
  const status = document.querySelector('[data-gpused-assistant-status]');
  const scriptUrl = loader.dataset.assistantScriptUrl;
  const styleUrl = loader.dataset.assistantStyleUrl;
  const apiUrl = loader.dataset.apiUrl;
  const mascotUrl = loader.dataset.mascotUrl;
  let loadPromise;

  const deferProductLauncher = () => {
    if (!launcher || launcher.dataset.gpusedDeferOnProduct !== 'true') return;

    const revealLauncher = () => {
      launcher.hidden = false;
      window.removeEventListener('scroll', handleProductScroll);
    };

    const handleProductScroll = () => {
      const threshold = Math.min(280, Math.max(180, window.innerHeight * 0.28));
      if (window.scrollY >= threshold) revealLauncher();
    };

    launcher.hidden = true;
    handleProductScroll();
    if (launcher.hidden) {
      window.addEventListener('scroll', handleProductScroll, { passive: true });
    }
  };

  const announce = (message) => {
    if (status) status.textContent = message;
  };

  const setLoading = (loading) => {
    if (!launcher) return;
    launcher.disabled = loading;
    launcher.setAttribute('aria-busy', String(loading));
  };

  const configureAssistant = () => {
    window.GPUsedAssistantConfig = {
      ...window.GPUsedAssistantConfig,
      apiUrl: window.GPUsedAssistantConfig?.apiUrl || apiUrl,
      mascotUrl: window.GPUsedAssistantConfig?.mascotUrl || mascotUrl,
    };
  };

  const loadStylesheet = () =>
    new Promise((resolve, reject) => {
      let stylesheet = document.querySelector('[data-gpused-assistant-style]');
      const shouldAppend = !stylesheet;

      if (stylesheet?.dataset.gpusedAssistantLoaded === 'true') {
        resolve(stylesheet);
        return;
      }

      if (!stylesheet) {
        stylesheet = document.createElement('link');
        stylesheet.dataset.gpusedAssistantStyle = '';
        stylesheet.href = styleUrl;
        stylesheet.rel = 'stylesheet';
      }

      stylesheet.addEventListener(
        'load',
        () => {
          stylesheet.dataset.gpusedAssistantLoaded = 'true';
          resolve(stylesheet);
        },
        { once: true },
      );
      stylesheet.addEventListener('error', () => reject(new Error('Assistant stylesheet failed')), {
        once: true,
      });
      if (shouldAppend) document.head.appendChild(stylesheet);
    });

  const loadScript = () =>
    new Promise((resolve, reject) => {
      let script = document.querySelector('[data-gpused-assistant-script]');
      const shouldAppend = !script;

      if (script?.dataset.gpusedAssistantLoaded === 'true') {
        resolve(script);
        return;
      }

      if (!script) {
        script = document.createElement('script');
        script.dataset.gpusedAssistantScript = '';
        script.dataset.apiUrl = apiUrl || '';
        script.src = scriptUrl;
        script.async = true;
      }

      script.addEventListener(
        'load',
        () => {
          script.dataset.gpusedAssistantLoaded = 'true';
          resolve(script);
        },
        { once: true },
      );
      script.addEventListener('error', () => reject(new Error('Assistant script failed')), {
        once: true,
      });
      if (shouldAppend) document.body.appendChild(script);
    });

  const loadAssistant = (openAfterLoad = false) => {
    if (!scriptUrl || !styleUrl) {
      announce('The assistant is unavailable. Please use the contact page.');
      return Promise.reject(new Error('Assistant URLs are missing'));
    }

    if (window.GPUsedAssistant?.status === 'ready') {
      if (openAfterLoad) window.GPUsedAssistant.open();
      if (launcher) launcher.hidden = true;
      return Promise.resolve(window.GPUsedAssistant);
    }

    if (loadPromise) {
      return loadPromise.then((assistant) => {
        if (openAfterLoad) assistant.open();
        return assistant;
      });
    }

    configureAssistant();
    setLoading(true);
    announce('Loading the GPUsed AI Assistant.');

    loadPromise = loadStylesheet()
      .then(() => loadScript())
      .then(() => {
        if (!window.GPUsedAssistant || typeof window.GPUsedAssistant.open !== 'function') {
          throw new Error('Assistant API is unavailable');
        }

        if (launcher) launcher.hidden = true;
        announce('');
        if (openAfterLoad) window.GPUsedAssistant.open();
        return window.GPUsedAssistant;
      })
      .catch((error) => {
        loadPromise = undefined;
        document
          .querySelectorAll(
            '[data-gpused-assistant-style]:not([data-gpused-assistant-loaded="true"]), ' +
              '[data-gpused-assistant-script]:not([data-gpused-assistant-loaded="true"])',
          )
          .forEach((element) => element.remove());
        announce('The assistant could not load. Please try again.');
        throw error;
      })
      .finally(() => setLoading(false));

    return loadPromise;
  };

  launcher?.addEventListener('click', () => {
    loadAssistant(true).catch(() => {});
  });

  deferProductLauncher();

  window.GPUsedAssistantLoader = Object.freeze({
    load: () => loadAssistant(false),
    open: () => loadAssistant(true),
  });
})();
